import * as _ from 'lodash';

import {
  AttachmentList,
  CommentList,
  ComponentData,
  ComponentSessionConfig,
  MemoDetail,
  MemoListDetail,
} from '../model/memo.model';
import { Observable, Subject, of } from 'rxjs';
import { map } from 'rxjs/operators';

import { API_URL } from '../../../shared/service/api.constant';
import { ApiService } from 'src/app/core/http/api.service';
import { HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { UploadMemoPayload } from '../model/template.model';
import { customAlphabet } from 'nanoid';

const CRM_PLUS_LOCKED_FLAGS = [
  'can_publish',
  'can_edit',
  'can_recall',
  'can_cancel_contract',
  'can_revise',
  'can_upload_attachment',
];

@Injectable({
  providedIn: 'root',
})
export class MemoService {
  headers: HttpHeaders;
  historyText: string;
  loaList: any;
  private setLoa = new Subject();
  data = this.setLoa.asObservable();

  changeTable: any;
  private setTable = new Subject();
  tableData = this.setTable.asObservable();
  requestPo: Observable<any>;

  private setFormat = new Subject();
  inputDateFormat = this.setFormat.asObservable();

  private setWidthForDate = new Subject();
  widthForDate = this.setWidthForDate.asObservable();

  private setComment = new Subject();
  $comment = this.setComment.asObservable();

  constructor(
    private http: ApiService,
    private translate: TranslateService,
  ) {}

  getDepartmentList(data?: { [type: string]: string }) {
    return this.http.get(API_URL.departments, data);
  }

  previewMemoPDF(data: { [type: string]: string }) {
    return this.http.pdfPost(API_URL.memo_preview, data);
  }

  // My task
  getMyTaskList(params?: { [type: string]: string }) {
    return this.http.get(API_URL.my_task, params);
  }

  getMyTaskBadge(params?: { [type: string]: string }) {
    return this.http.get(API_URL.my_task + 'badge/', params);
  }

  /**
   * Get the unique sessions property from `ComponentData`.
   * If all components is undefined a session then it return empty array.
   */
  getInputComponentSessions(
    inputComponents: ComponentData[],
    ignoreUndefinedGroup = true,
  ): ComponentSessionConfig[] {
    const sessionNames: string[] = [];
    const sessions: ComponentSessionConfig[] = [];
    const retrieveSessionName = (
      session: string | ComponentSessionConfig,
    ) => {
      if (typeof session === 'object') {
        return session.sessionName;
      }
      return session;
    };
    inputComponents.forEach((component) => {
      const sessionName = retrieveSessionName(component.session);
      if (ignoreUndefinedGroup && !sessionName) {
        return;
      }
      const isDuplicated = sessionNames.includes(sessionName);
      if (!isDuplicated && component.componentName != null) {
        sessionNames.push(sessionName);
        sessions.push(this.rewrapSessionConfig(component.session));
      }
    });
    return sessions;
  }

  // RealMemo
  getGeneralMemoList(params?: {
    [type: string]: string;
  }): Observable<MemoListDetail> {
    return this.http.get<MemoListDetail>(
      API_URL.general_memo,
      params,
    );
  }

  getQrMemoRedirect(id: number, data?) {
    return this.http.post(API_URL.memos + id + '/qrlogger/', data);
  }

  getMemoListCSV(data) {
    // url นี้จะไม่ return file อีกแล้ว แต่จะ return celery task id มาแทน
    return this.http.get(API_URL.general_memo + 'excel/', data);
  }

  getMemoFiles(data) {
    return this.http.getBlob(
      API_URL.general_memo + 'download-multiple-pdf/',
      data,
    );
  }

  getMemoReferenceById(id) {
    return this.http.get(API_URL.memos + id + '/references/');
  }

  getMemoQrViewingLogByMemoId(id) {
    return this.http.get(API_URL.memos + id + '/qr-list/');
  }

  updateMemoReferenceById(id, data) {
    return this.http.post(
      API_URL.memos + id + '/update-references/',
      data,
    );
  }

  // Action
  createMemo(data: UploadMemoPayload): Observable<MemoDetail> {
    return this.http.post(API_URL.memos, data);
  }

  updateMemo(id, data): Observable<MemoDetail> {
    return this.http.patch(API_URL.memos + id + '/', data);
  }

  updateAnnouncement(id, data, header?) {
    return this.http.post(
      API_URL.memos + id + '/announcement/',
      data,
      header,
    );
  }

  publishMemo(id, data?) {
    return this.http.post(API_URL.memos + id + '/publish/', data);
  }

  extendMemo(id, data) {
    return this.http.post(API_URL.memos + id + '/extend/', data);
  }

  reviseMemo(id) {
    return this.http.post(API_URL.memos + id + '/revise/', {});
  }

  duplicateMemo(id, data) {
    return this.http.post<{
      id: number;
      re_init_dynamic_loa: boolean;
    }>(API_URL.memos + id + '/duplicate/', data);
  }

  recallMemo(id, data?) {
    return this.http.post(API_URL.memos + id + '/recall/', data);
  }

  recallLoaMemo(id, data) {
    return this.http.post(API_URL.memos + id + '/recall-loa/', data);
  }

  approveMemo(id, data?) {
    return this.http.post(API_URL.memos + id + '/approve/', data);
  }

  rejectMemo(id, data?) {
    return this.http.post(API_URL.memos + id + '/reject/', data);
  }

  terminateMemo(id, data?) {
    return this.http.post(API_URL.memos + id + '/terminate/', data);
  }

  downloadMemo(id, data?) {
    if (!id) {
      return of();
    }
    return this.http.postResponseBlob(
      API_URL.memos + id + '/download-pdf/',
      data,
    );
  }

  getMemoAttachmentFile(params?: {
    [type: string]: string;
  }): Observable<AttachmentList> {
    return this.http.get<AttachmentList>(
      API_URL.memo_attachment,
      params,
    );
  }

  getMemoAttachmentEStamp(params?: {
    [type: string]: string;
  }): Observable<AttachmentList> {
    return this.http.get<AttachmentList>(
      API_URL.memo_attachment_e_stamp,
      params,
    );
  }

  uploadEStamp(memoId, formData: FormData) {
    return this.http.post(
      API_URL.contract + memoId + '/upload-e-stamp/',
      formData,
    );
  }

  customDownloadPDF(id, data?) {
    return this.http.postBlob(
      API_URL.memos + id + '/download-merge-pdf/',
      data,
    );
  }

  getMemoDetail(id: number): Observable<MemoDetail> {
    return this.http
      .get<MemoDetail>(API_URL.memos + id + '/')
      .pipe(map((memo) => this.lockCrmPlusMemo(memo)));
  }

  private lockCrmPlusMemo(memo: MemoDetail): MemoDetail {
    if (memo?.contract?.source === 'crm_plus') {
      CRM_PLUS_LOCKED_FLAGS.forEach((flag) => (memo[flag] = false));
    }
    return memo;
  }

  getMemoHistory(params?: { [type: string]: string }) {
    return this.http.get(API_URL.memos_history, params);
  }

  getHistoryLogCSV(data) {
    return this.http.getBlob(API_URL.memos_history + 'excel/', data);
  }

  getQrViewingLogCSV(id, data?) {
    return this.http.getBlob(
      API_URL.memos + id + '/qr-export-excel/',
      data,
    );
  }

  deleteMemo(id) {
    return this.http.delete(API_URL.memos + id + '/');
  }

  // Upload Blob

  uploadBlob(id, data) {
    return this.http.patch(API_URL.upload_memo_blob + id + '/', data);
  }
  uploadContractBlob(id, data) {
    return this.http.patch(
      API_URL.upload_contract_blob + id + '/',
      data,
    );
  }

  uploadMultiBlob(id, data) {
    return this.http.patch(
      API_URL.upload_memo_blob_multi + id + '/',
      data,
    );
  }

  uploadContractMultiBlob(id, data) {
    return this.http.patch(
      API_URL.upload_contract_blob_multi + id + '/',
      data,
    );
  }

  removeBlob(data) {
    return this.http.post(API_URL.remove_memo_blob, data);
  }

  removeContractBlob(data) {
    return this.http.post(API_URL.remove_contract_blob, data);
  }

  // Comment

  getCommentList(params): Observable<CommentList[]> {
    return this.http.get(API_URL.memo_comment, params);
  }

  createNewComment(data): Observable<CommentList> {
    return this.http.post(API_URL.memo_comment, data);
  }

  updateMemoRead(data) {
    return this.http.post(API_URL.memo_read, data);
  }

  deleteComment(id) {
    return this.http.delete(API_URL.memo_comment + id + '/');
  }

  // Attachment
  getMemoAttachment(params?: { [type: string]: string }) {
    return this.http.get(API_URL.memo_attachment, params);
  }

  removeMemoAttachment(id) {
    return this.http.delete(API_URL.memo_attachment + id + '/');
  }

  updateMemoAttchment(id, data) {
    return this.http.patch(API_URL.memo_attachment + id + '/', data);
  }

  uploadMemoAttachment(data) {
    return this.http.post(API_URL.memo_bulk_attachment, data);
  }

  downloadMemoAttachment(id, data?) {
    return this.http.post(
      API_URL.memo_attachment + id + '/download/',
      data,
    );
  }

  logDownloadFile(
    id,
    log_type: 'rejected' | 'attachment' | 'comment',
  ) {
    return this.http.post(
      API_URL.memos + id + '/log-download-file/',
      { log_type },
    );
  }

  // Verify Duplicated Memo Number
  verifyMemoNumber(params) {
    return this.http.get(API_URL.memo_number_verify, params);
  }

  printFile(url) {
    return this.http.printFile(url);
  }

  getMemoTypes() {
    const params = { type: 'memo_type' };
    return this.http.get(API_URL.dropdown, params);
  }

  getMemoUploadTypes() {
    const params = { type: 'memo_upload_type' };
    return this.http.get(API_URL.dropdown, params);
  }

  setLoadLoaList(department) {
    this.loaList = department;
    this.setLoa.next(this.loaList);
  }

  setInputDate(date) {
    this.setFormat.next(date);
  }

  setWidthDate(event?) {
    this.setWidthForDate.next(event);
  }

  getMemoRevised(params) {
    return this.http.get(API_URL.memo_revised, params);
  }

  // acknowledgement
  getAcknowledge(params) {
    return this.http.get('/api/acknowledges/', params);
  }

  getAcknowledgeCSV(data) {
    return this.http.getBlob('/api/acknowledges/excel/', data);
  }

  // download file
  createDownloadFile(data: any, filename: string): void {
    // for IE10+
    const blob = new Blob([data], { type: data.type });
    const url = window.URL.createObjectURL(blob);
    window.open(url);
  }

  rewrapSessionConfig(
    session: string | ComponentSessionConfig,
  ): ComponentSessionConfig {
    if (typeof session === 'string') {
      return { sessionName: session };
    }
    return session;
  }

  updateMemoDetail(id, data) {
    return this.http.patch(API_URL.memo_detail + id + '/', data);
  }

  // trash
  getFinishedMemoList(params?: {
    [type: string]: string;
  }): Observable<MemoListDetail> {
    return this.http.get<MemoListDetail>(
      API_URL.finished_memo,
      params,
    );
  }

  bulkTrashMemo(data) {
    return this.http.post(API_URL.bulk_trash_memo, data);
  }

  bulkUntrashMemo(data) {
    return this.http.post(API_URL.bulk_untrash_memo, data);
  }

  bulkPermanentlyDeleteMemo(data) {
    return this.http.post(API_URL.bulk_permanently_delete_memo, data);
  }

  getFinishedMemoListCSV(data) {
    return this.http.get(API_URL.finished_memo + 'excel/', data);
  }

  getFinishedMemoFiles(data) {
    return this.http.get(
      API_URL.finished_memo + 'download-multiple-pdf/',
      data,
    );
  }

  exportMemoToGDrive(data) {
    return this.http.post(
      API_URL.finished_memo + 'export-gdrive/',
      data,
    );
  }
  getNdidCredit() {
    return this.http.get('/api/ndid/credit/');
  }

  getThaicomCredit() {
    return this.http.get('/api/thaicom/credit/');
  }

  removeAllMemoAttachment(memo_id) {
    return this.http.post('/api/memo-attachments/remove-all/', {
      memo_id,
    });
  }

  updateCategory(id: number, data) {
    return this.http.patch(
      API_URL.memos + id + '/update-categories/',
      data,
    );
  }

  validateFiles(data) {
    return this.http.post('/api/validate-files/', data);
  }

  getContractList(
    params?: {
      [type: string]: string;
    },
    isCrmPlus = false,
  ): Observable<MemoListDetail> {
    return this.http.get<MemoListDetail>(
      this.contractListUrl(isCrmPlus),
      params,
    );
  }
  getContractListCSV(data, isCrmPlus = false) {
    return this.http.get(
      this.contractListUrl(isCrmPlus) + 'excel/',
      data,
    );
  }

  getContractFiles(data, isCrmPlus = false) {
    return this.http.getBlob(
      this.contractListUrl(isCrmPlus) + 'download-multiple-pdf/',
      data,
    );
  }

  private contractListUrl(isCrmPlus: boolean): string {
    return isCrmPlus ? API_URL.crm_contract : API_URL.contract;
  }

  getRequestEvidence(id, data, header?) {
    return this.http.post(
      '/api/memos/contract/' + id + '/request-evidences/',
      data,
      header,
    );
  }
  getSelfAttachment(id, data, header?) {
    return this.http.post(
      '/api/memos/contract/' + id + '/self-request-attachments/',
      data,
      header,
    );
  }

  uploadRequestAttachment(id, data, header?): Observable<any> {
    return this.http.post(
      '/api/memos/contract/' +
        id +
        '/self-upload-request-attachments/',
      data,
      header,
    );
  }

  uploadSelfieVideo(id, fd: FormData, header?): Observable<any> {
    return this.http.post(
      '/api/memos/contract/' + id + '/self-upload-selfie-video/',
      fd,
      header,
    );
  }
  testConvertAndResizeVideo(fd: FormData, header?): Observable<any> {
    return this.http.postResponseBlob(
      '/api/memos/contract/test-resize-convert-video/',
      fd,
      header,
    );
  }
  getEvidence(id) {
    return this.http.get('/api/memos/contract/' + id + '/evidence/');
  }

  getVideoSelfie(id, data) {
    return this.http.post(
      '/api/memos/contract/' + id + '/selfie-video/',
      data,
    );
  }

  getEvidenceAttachment(id, data) {
    return this.http.post(
      '/api/memos/contract/' + id + '/request-attachments/',
      data,
    );
  }

  postResubmit(id, data) {
    return this.http.post(
      '/api/memos/contract/' + id + '/resubmit-request/',
      data,
    );
  }

  confirmEvidence(id, data) {
    return this.http.post(
      '/api/memos/contract/' + id + '/confirm-evidence/',
      data,
    );
  }

  checkCredit(id, data) {
    return this.http.post(
      '/api/memos/contract/' + id + '/check-credit/',
      data,
    );
  }

  changeSmsOtp(
    id,
    data: { member_id: number; phone_number: string },
  ) {
    return this.http.patch(
      API_URL.memos + id + '/change-sms-otp/',
      data,
    );
  }

  updateMemoCC(id, data) {
    return this.http.post(API_URL.memos + id + '/update-cc/', data);
  }

  updateMultiMemoCC(data, mode: 'add' | 'update' = 'update') {
    let url = 'update-multi-cc/';
    if (mode === 'add') {
      url = 'add-cc/';
    }
    return this.http.post(API_URL.memos + url, data);
  }

  convertPreviewSignature(memo) {
    const previewSignature = [];
    const memoType =
      memo.memo_type === 'contract' ? memo.contract : memo.upload;
    memoType.signature_position.forEach((value) => {
      if (
        memo.current_level === value?.level &&
        memo.can_approve &&
        memo.current_member_seq === value.member_seq &&
        value.member_seq !== null
      ) {
        value.can_edit_widget = true;
        previewSignature.push(value);
      }
    });
    return _.uniqWith(previewSignature, _.isEqual);
  }

  errorMessage(previewSignature, memo) {
    const findStamp = [];
    if (Array.isArray[previewSignature]) {
      previewSignature?.forEach((v) => {
        if (
          memo.current_member_seq === v.member_seq &&
          v.member_seq !== null &&
          memo.current_level === v.level
        ) {
          if (v?.stamp_positions && v?.stamp_positions?.length > 0) {
            v.stamp_positions.forEach((stamp) => {
              if (
                stamp.req &&
                Object.keys(stamp.stampFiles).length === 0
              ) {
                findStamp.push(stamp);
              }
            });
          }
        }
      });
    }

    const totalError = [];

    if (findStamp.length > 0) {
      totalError.push(
        this.translate.instant('UPLOAD.Please upload stamp'),
      );
    }
    const finalMessage = _.union(totalError);
    return finalMessage;
  }

  cancelContract(id, data) {
    return this.http.post(
      '/api/memos/contract/' + id + '/cancel/',
      data,
    );
  }

  setCommentEvent(event?) {
    this.setComment.next(event);
  }
}

@Injectable({
  providedIn: 'root',
})
export class UtilsService {
  createNanoId(digits = 12): string {
    return customAlphabet(
      'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789',
      digits,
    )();
  }
}
